<template>
  <div class="privacy">
    <div class="privacy__header">
      <img :src="logoSrc" :alt="logoAlt" class="privacy__logo" />
    </div>
    <div class="privacy__content">
      <h2>Privacy policy</h2>
      <p>
        This Privacy Policy (&quot;Policy&quot;) describes how Olympian By
        Design (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), a software
        company selling retail direct-to-consumer products, collects, uses,
        discloses, and protects personal information from individuals
        (&quot;you&quot; or &quot;your&quot;) who visit or use our website,
        mobile applications, and related services (collectively, the
        &quot;Services&quot;).
      </p>
      <p>
        By accessing or using our Services, you acknowledge that you have read
        and understood this Policy and consent to the collection, use, and
        disclosure of your personal information as described herein. If you do
        not agree with this Policy, please refrain from using our Services.
      </p>
      <h5>Information We Collect</h5>
      <p>
        We may collect various types of personal information from you, including
        but not limited to:
      </p>

      <p>
        1.1. Personal Information: This includes your name, email address, phone
        number, mailing address, and other contact information you provide to
        us.
      </p>

      <p>
        1.2. Payment Information: When you make a purchase, we collect payment
        information, such as credit card details or other financial account
        information necessary to process your transaction securely.
      </p>

      <p>
        1.3. Device and Usage Information: We automatically collect information
        about the device you use to access our Services, such as IP address,
        browser type, operating system, and other technical details. We may also
        collect usage information, such as the pages you visit, the features you
        use, and the actions you take while using our Services.
      </p>

      <p>
        1.4. Cookies and Similar Technologies: We may use cookies, web beacons,
        and similar technologies to collect information about your browsing
        activities and preferences. You can control the use of cookies through
        your browser settings, but please note that disabling cookies may impact
        your experience with our Services.
      </p>

      <h5>Use of Personal Information</h5>
      <p>
        We may use the personal information we collect for the following
        purposes:
      </p>

      <p>
        2.1. Providing and Improving our Services: We use your personal
        information to deliver and maintain the functionality of our Services,
        respond to your inquiries, and provide you with customer support. We may
        also use your information to enhance and improve our Services, develop
        new features, and personalize your experience.
      </p>

      <p>
        2.2. Order Processing and Fulfillment: We use your personal and payment
        information to process and fulfill your orders, including order
        confirmation, shipment, and customer service related to your purchase.
      </p>

      <p>
        2.3. Communication: We may use your contact information to communicate
        with you regarding our Services, updates, promotions, and other relevant
        information. You can opt out of receiving marketing communications at
        any time by following the unsubscribe instructions provided in the
        communication.
      </p>

      <p>
        2.4. Legal Compliance: We may process your personal information to
        comply with applicable laws, regulations, legal processes, or
        enforceable governmental requests.
      </p>

      <h5>Information Sharing and Disclosure</h5>
      <p>
        We may share your personal information with the following parties, only
        to the extent necessary to provide our Services:
      </p>

      <p>
        3.1. Service Providers: We may engage trusted third-party service
        providers who assist us in delivering our Services, such as payment
        processors, shipping partners, IT infrastructure providers, and customer
        support services. These service providers are contractually bound to
        handle your personal information securely and are prohibited from using
        it for any other purpose.
      </p>

      <p>
        3.2. Business Transfers: In the event of a merger, acquisition, or sale
        of all or a portion of our assets, your personal information may be
        transferred as part of the transaction. We will notify you via email or
        a prominent notice on our website if such a transfer occurs.
      </p>

      <p>
        3.3. Legal Requirements: We may disclose your personal information to
        comply with legal obligations, respond to lawful requests from public
        authorities, or protect our rights, privacy, safety, or property.
      </p>

      <h5>Data Retention</h5>

      <p>
        We retain your personal information for as long as necessary to fulfill
        the purposes outlined in this Policy, unless a longer retention period
        is required or permitted by law. Once the retention period expires, we
        will securely delete or anonymize your personal information.
      </p>

      <h5>Data Security</h5>
      <p>
        We employ industry-standard security measures to protect your personal
        information from unauthorized access, alteration, disclosure, or
        destruction. However, please note that no method of transmission or
        storage is 100% secure, and we cannot guarantee the absolute security of
        your information.
      </p>

      <h5>Your Rights and Choices</h5>
      <p>
        You have certain rights and choices regarding your personal information:
      </p>

      <p>
        6.1. Access and Update: You have the right to access, correct, or update
        your personal information. You can review and modify your account
        information by logging into your account or by contacting us using the
        information provided in the &quot;Contact Us&quot; section below.
      </p>

      <p>
        6.2. Opt-Out: You can opt out of receiving marketing communications from
        us by following the instructions provided in the communication. However,
        we may still send you transactional or administrative messages related
        to our Services.
      </p>

      <p>
        6.3. Do Not Track: Our Services do not respond to &quot;Do Not
        Track&quot; signals. However, you can disable cookies and tracking
        technologies through your browser settings.
      </p>

      <h5>Children Privacy</h5>
      <p>
        Our Services are not intended for individuals under the age of 16. We do
        not knowingly collect personal information from children without
        verifiable parental consent. If we become aware that we have collected
        personal information from a child without parental consent, we will take
        steps to remove the information promptly. If you believe that we may
        have collected information from a child without parental consent, please
        contact us using the information provided below.
      </p>

      <h5>
        Changes to this Privacy Policy
      </h5>
      <p>
        We may update this Privacy Policy from time to time to reflect changes
        in our practices or legal requirements. We will notify you of any
        material changes by posting the updated Policy on our website or through
        other communication channels. We encourage you to review this Policy
        periodically for the latest information on our privacy practices.
      </p>

      <h5>Contact Us</h5>
      <p>
        If you have any questions, concerns, or requests regarding this Privacy
        Policy or our privacy practices, please contact us at:
      </p>

      <strong>Olympian By Design</strong>
      <ul>
        <li>
          Warrior Athlete Inc 2515 Waukegan Rd #727 Deerfield, IL 60015
          “…following the laws of Illinois”
        </li>
        <li>
          Email
          <a href="mailto:D@OlympianbyDesign.com">D@OlympianbyDesign.com</a>
        </li>
      </ul>

      <p>
        Note: This sample Privacy Policy is provided for informational purposes
        only and does not constitute legal advice. It is important to consult
        with a legal professional to ensure compliance with applicable privacy
        laws and regulations based on your specific circumstances.
      </p>

      <h3>TERMS OF ENGAGEMENT</h3>

      <p>
        These Terms of Engagement (&quot;Terms&quot;) govern your use of the
        services provided by Olympian By Design (&quot;we,&quot; &quot;us,&quot;
        or &quot;our&quot;), a software company selling retail
        direct-to-consumer products. By accessing or using our services,
        including but not limited to our website, mobile applications, and
        related services (collectively, the &quot;Services&quot;), you agree to
        be bound by these Terms.
      </p>

      <h5>Acceptance of Terms</h5>
      <p>
        By using our Services, you affirm that you have read, understood, and
        agree to comply with these Terms. If you do not agree to these Terms,
        please refrain from using our Services.
      </p>

      <h5>Scope of Services</h5>
      <p>
        Our Services provide you with access to our retail direct-to-consumer
        products, including software applications, digital content, and related
        features. We may also offer additional services, such as customer
        support, updates, and other related features to enhance your user
        experience.
      </p>

      <h5>User Accounts and Responsibilities</h5>
      <p>
        3.1. Account Creation: To access certain features of our Services, you
        may be required to create a user account. You agree to provide accurate,
        current, and complete information during the registration process and to
        update your information as necessary to ensure its accuracy.
      </p>

      <p>
        3.2. Account Security: You are responsible for maintaining the
        confidentiality of your account credentials and for any activity that
        occurs under your account. You agree to notify us immediately of any
        unauthorized access or use of your account. We are not liable for any
        loss or damage arising from your failure to comply with this security
        obligation.
      </p>

      <p>
        3.3. Prohibited Activities: When using our Services, you agree not to:
      </p>

      <h5>Violate any applicable laws, regulations, or third-party rights.</h5>
      <p>
        Engage in unauthorized access, hacking, data mining, or any other
        illegal or unauthorized activities. Interfere with or disrupt the
        integrity or performance of our Services. Transmit or distribute
        viruses, malware, or any other harmful code.
      </p>

      <p>
        Engage in any activity that could damage our reputation or goodwill.
      </p>

      <h5>Intellectual Property Rights</h5>
      <p>
        4.1. Ownership: We retain all rights, title, and interest in and to our
        Services, including but not limited to software, content, trademarks,
        logos, and other intellectual property. These Terms do not grant you any
        ownership rights to our intellectual property.
      </p>

      <p>
        4.2. License: Subject to your compliance with these Terms, we grant you
        a limited, non-exclusive, non- transferable, revocable license to access
        and use our Services for your personal, non-commercial use. This license
        does not permit you to modify, reproduce, distribute, create derivative
        works, or reverse engineer our Services.
      </p>

      <h5>Payment and Pricing</h5>
      <p>
        5.1. Pricing and Payment: The prices for our products and services are
        clearly indicated on our website or during the checkout process. By
        purchasing our products, you agree to pay the specified price, including
        any applicable taxes and fees. We reserve the right to change our prices
        at any time, but any changes will not affect orders that have already
        been placed.
      </p>

      <p>
        5.2. Payment Processing: We may use third-party payment processors to
        facilitate your payment. You agree to provide accurate and valid payment
        information and authorize us to charge the specified amount for your
        purchase. We are not responsible for any errors, delays, or payment
        issues arising from the third-party payment processor.
      </p>

      <h5>Limitation of Liability</h5>
      <p>
        6.1. Disclaimer: Our Services are provided on an &quot;as-is&quot; and
        &quot;as available&quot; basis without any warranties, whether expressed
        or implied. We do not warrant that our Services will be error-free,
        uninterrupted, or free from viruses or other harmful components. You use
        our Services at your own risk.
      </p>

      <p>
        6.2. Limitation of Liability: To the maximum extent permitted by
        applicable law, we shall not be liable for any direct, indirect,
        incidental, consequential, or special damages arising out of or in
        connection with your use or inability to use our Services, even if we
        have been advised of the possibility of such damages.
      </p>

      <h5>Governing Law and Dispute Resolution</h5>

      <p>
        7.1. Governing Law: These Terms shall be governed by and construed in
        accordance with the laws of [Jurisdiction]. Any disputes arising out of
        or relating to these Terms or our Services shall be subject to the
        exclusive jurisdiction of the courts in [Jurisdiction].
      </p>

      <p>
        7.2. Dispute Resolution: In the event of a dispute or claim arising
        under these Terms, you agree to first attempt to resolve the dispute
        informally by contacting us directly. If we are unable to resolve the
        dispute informally, both parties agree to resolve the dispute through
        binding arbitration, conducted in accordance with the rules of
        [Arbitration Organization]. The decision of the arbitrator shall be
        final and binding.
      </p>

      <h5>Amendments to the Terms</h5>
      <p>
        We reserve the right to modify or update these Terms at any time without
        prior notice. Any changes will be effective immediately upon posting the
        updated Terms on our website. Your continued use of our Services after
        the changes will constitute your acceptance of the revised Terms.
      </p>

      <h5>Contact Us</h5>
      <p>
        If you have any questions, concerns, or feedback regarding these Terms,
        please contact us at:
      </p>

      <strong>Olympian By Design</strong>
      <ul>
        <li>
          Warrior Athlete Inc 2515 Waukegan Rd #727 Deerfield, IL 60015
          “…following the laws of Illinois”
        </li>
        <li>
          Email:
          <a href="mailto:D@OlympianbyDesign.com">D@OlympianbyDesign.com</a>
        </li>
      </ul>

      <p>Please allow a reasonable time for us to respond to your inquiries.</p>
    </div>
  </div>
</template>

<script>
import extendedConfig from '../tenants'
// @ is an alias to /src
export default {
  name: 'PrivacyPolicy',
  components: {},
  data: () => ({}),
  computed: {
    logoSrc() {
      if (extendedConfig.VUE_TENANT_ID=='brainhealthinitiative') return require('@/assets/bhi_logo.png') // TODO: require(extendedConfig.VUE_MAIN_LOGO) is not working
      if (extendedConfig.VUE_TENANT_ID=='olympianbydesign') return require('@/assets/obd_logo.png')
      return extendedConfig.VUE_MAIN_LOGO || require('@/assets/logo-keep-rising-white.png')
    },
    logoAlt() {
      return extendedConfig.VUE_LOGO_ALT || extendedConfig.VUE_MAIN_TITLE
    },
  },
}
</script>

<style lang="scss">
.privacy {
  &__header {
    max-width: 80%;
    padding: 31px 29px;
    text-align: left;
    margin: 0 auto;
  }
  &__logo {
    max-width: 200px;
  }
  &__content {
    max-width: 80%;
    padding: 31px 29px;
    margin: 0 auto;

    strong {
      color: var(--bh-blue--1);
    }

    a {
      color: var(--bh-blue--1);
      font-weight: 700;
      text-decoration: none;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style: none;

      li {
        position: relative;
        padding-left: 16px;
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: var(--bh-blue--1);
        }
      }
    }
  }
}
</style>
